import { fetchAgenticSnapshot, refreshAgenticSnapshot, type AgenticSnapshot } from "../api/agenticClient";
import { fetchPaperAgentsList } from "../api/agentsPaperClient";
import {
  accountScopeCatalog,
  accountScopeGeneration,
  accountScopeState,
  ensureAccountScopeCatalog,
  paperAccountsEnabled,
  subscribeAccountScope,
  visibleScopeAccounts,
} from "./accountScope";
import { loadAgenticFeatureFlag } from "./agenticFeatures";
import {
  AGENTIC_ACCOUNT_CHANGED_EVENT,
  activeAccountLabelFromSnapshot,
  buyingPowerForAccountNumber,
  buyingPowerFromActiveAccount,
  cashEquivalentForAccountNumber,
  cashEquivalentFromActiveAccount,
} from "./agenticPortfolioCash";
import type { LedgerAccount } from "./ownLedgerClient";
import { AGENTIC_SNAPSHOT_REFRESHED_EVENT, notifyAgenticSnapshotRefreshed } from "./agenticRefreshSettings";
import { fmtUsd, fmtUsdWhole } from "./watchlistData";

const HEADER_BP_POLL_MS = 30_000;

let activeRefresh: (() => void) | null = null;
let activeLiveRefresh: (() => void) | null = null;

function scopedAccounts(): LedgerAccount[] {
  const picked = new Set(accountScopeState().accountIds);
  const rows = visibleScopeAccounts();
  if (!picked.size) return rows;
  return rows.filter((account) => picked.has(account.account_id));
}

function isRobinhoodLedgerAccount(account: LedgerAccount): boolean {
  const broker = String(account.broker_id || account.broker || "").toLowerCase();
  return broker === "robinhood" || account.account_id.startsWith("robinhood:");
}

function sumParts(parts: Array<{ label: string; amount: number }>): {
  amount: number | null;
  label: string | null;
} {
  if (!parts.length) return { amount: null, label: null };
  const amount = Math.round(parts.reduce((sum, part) => sum + part.amount, 0) * 100) / 100;
  const label = parts
    .map((part) => part.label)
    .filter(Boolean)
    .join(", ");
  return { amount, label: label || null };
}

function liveBuyingPower(
  snapshot: AgenticSnapshot | null,
  accounts: LedgerAccount[],
): { amount: number | null; label: string | null; rhBuyingPower: number | null } {
  const robinhood = accounts.filter(isRobinhoodLedgerAccount);
  if (!robinhood.length) {
    if (accounts.length || accountScopeCatalog().length) {
      return { amount: null, label: null, rhBuyingPower: null };
    }
    return {
      amount: cashEquivalentFromActiveAccount(snapshot),
      label: activeAccountLabelFromSnapshot(snapshot),
      rhBuyingPower: buyingPowerFromActiveAccount(snapshot),
    };
  }
  const parts: Array<{ label: string; amount: number; rhBuyingPower: number | null }> = [];
  for (const account of robinhood) {
    const ref = String(account.broker_account_ref || "").trim();
    const amount = ref
      ? cashEquivalentForAccountNumber(snapshot, ref)
      : account.account_id === "robinhood:default"
        ? cashEquivalentFromActiveAccount(snapshot)
        : null;
    if (amount == null) continue;
    const rhBuyingPower = ref
      ? buyingPowerForAccountNumber(snapshot, ref)
      : buyingPowerFromActiveAccount(snapshot);
    parts.push({
      label: account.label || account.short_name || "Robinhood",
      amount,
      rhBuyingPower,
    });
  }
  if (!parts.length && robinhood.length === 1) {
    const amount = cashEquivalentFromActiveAccount(snapshot);
    if (amount != null) {
      return {
        amount,
        label: robinhood[0]?.label || activeAccountLabelFromSnapshot(snapshot),
        rhBuyingPower: buyingPowerFromActiveAccount(snapshot),
      };
    }
  }
  const summed = sumParts(parts);
  const rhParts = parts
    .map((part) => part.rhBuyingPower)
    .filter((value): value is number => value != null);
  return {
    ...summed,
    rhBuyingPower: rhParts.length
      ? Math.round(rhParts.reduce((sum, value) => sum + value, 0) * 100) / 100
      : null,
  };
}

function paintHeaderBuyingPower(
  host: ParentNode,
  amount: number | null,
  opts: { paper: boolean; label: string | null; rhBuyingPower?: number | null },
): void {
  const btn = host.querySelector<HTMLElement>("[data-sfe-header-bp]");
  const valueEl = host.querySelector<HTMLElement>("[data-header-bp-value]");
  if (!btn || !valueEl) return;
  btn.classList.toggle("is-paper", opts.paper);

  if (amount == null) {
    btn.hidden = true;
    valueEl.textContent = "—";
    btn.removeAttribute("data-tip");
    btn.removeAttribute("title");
    btn.setAttribute("aria-label", opts.paper ? "Paper buying power unavailable" : "Buying power unavailable");
    return;
  }

  const compact = fmtUsdWhole(amount);
  const exact = fmtUsd(amount);
  const rh =
    !opts.paper &&
    opts.rhBuyingPower != null &&
    Math.abs(opts.rhBuyingPower - amount) > 0.5
      ? opts.rhBuyingPower
      : null;
  const tip = opts.paper
    ? opts.label
      ? `Paper buying power · ${opts.label}: ${exact}`
      : `Paper buying power: ${exact}`
    : [
        opts.label
          ? `Spendable cash equivalent · ${opts.label}: ${exact}`
          : `Spendable cash equivalent: ${exact}`,
        rh != null ? `Robinhood buying power ${fmtUsd(rh)} (often 2× cash).` : null,
      ]
        .filter(Boolean)
        .join(" ");

  btn.hidden = false;
  valueEl.textContent = compact;
  btn.title = tip;
  btn.setAttribute("data-tip", tip);
  btn.setAttribute("aria-label", tip);
}

async function fetchPaperBuyingPower(accountIds: string[]): Promise<{
  amount: number | null;
  label: string | null;
  accounts: Array<{ label?: string; buyingPower?: number | null }>;
}> {
  const query = accountIds.length
    ? `?account_ids=${encodeURIComponent(accountIds.join(","))}`
    : "";
  const res = await fetch(`/own-ledger/webull/paper/buying-power${query}`, {
    credentials: "include",
    headers: { Accept: "application/json", "Cache-Control": "no-cache" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Paper buying power failed (${res.status})`);
  const data = (await res.json()) as {
    buyingPower?: number | null;
    accounts?: Array<{ label?: string; buyingPower?: number | null }>;
  };
  const accounts = (data.accounts ?? []).filter((row) => row.buyingPower != null);
  const label =
    accounts.length === 1
      ? String(accounts[0]?.label || "").trim() || null
      : accounts.length > 1
        ? accounts
            .map((row) => String(row.label || "").replace(/^Paper ·\s*/, "").trim())
            .filter(Boolean)
            .join(", ")
        : null;
  const amount = typeof data.buyingPower === "number" && Number.isFinite(data.buyingPower) ? data.buyingPower : null;
  return { amount, label, accounts };
}

async function paperBuyingPower(accounts: LedgerAccount[]): Promise<{
  amount: number | null;
  label: string | null;
}> {
  const webullIds = accounts
    .filter((account) => account.account_id.startsWith("webull:paper:"))
    .map((account) => account.account_id);
  const agentIds = new Set(
    accounts.filter((account) => account.account_id.startsWith("paper_agent_")).map((account) => account.account_id),
  );
  const parts: Array<{ label: string; amount: number }> = [];
  if (webullIds.length) {
    try {
      const next = await fetchPaperBuyingPower(webullIds);
      for (const row of next.accounts ?? []) {
        if (typeof row.buyingPower !== "number" || !Number.isFinite(row.buyingPower)) continue;
        parts.push({
          label: String(row.label || "").replace(/^Paper ·\s*/, "").trim() || "Webull paper",
          amount: row.buyingPower,
        });
      }
    } catch {
      /* paper-agent cash can still paint */
    }
  }
  if (agentIds.size) {
    const list = await fetchPaperAgentsList();
    const seen = new Set<string>();
    for (const agent of list.agents) {
      const accountId = String(agent.accountId || "");
      if (!agentIds.has(accountId) || seen.has(accountId)) continue;
      if (agent.kind === "advisor") continue;
      if (typeof agent.buyingPower !== "number" || !Number.isFinite(agent.buyingPower)) continue;
      seen.add(accountId);
      parts.push({ label: agent.name || accountId, amount: agent.buyingPower });
    }
  }
  return sumParts(parts);
}

export function headerBuyingPowerHtml(): string {
  return `
    <button type="button" class="sfe-header-buying-power" data-sfe-header-bp data-action="header-buying-power" hidden aria-label="Buying power">
      <span class="sfe-header-buying-power-label">BP:</span>
      <span class="sfe-header-buying-power-value" data-header-bp-value>…</span>
    </button>`;
}

export function refreshHeaderBuyingPower(): void {
  activeRefresh?.();
}

/** After trades: re-read stored snapshot immediately, then pull Robinhood and paint again. */
export function refreshHeaderBuyingPowerLive(): void {
  activeLiveRefresh?.();
}

export function startHeaderBuyingPower(
  root: ParentNode,
  opts: { onOpenImport?: () => void } = {},
): () => void {
  const host =
    root.querySelector<HTMLElement>("[data-sfe-header-bp]")?.closest(".sfe-web-shell, .sfe-mobile-host") ??
    root;

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    const btn = target?.closest?.("[data-action='header-buying-power']");
    if (!btn || !host.contains(btn)) return;
    opts.onOpenImport?.();
  };
  host.addEventListener("click", onClick);

  let inflight: Promise<void> | null = null;
  let queued = false;
  let cancelled = false;
  let liveInflight: Promise<void> | null = null;
  let lastLive: { amount: number | null; label: string | null; rhBuyingPower: number | null } = {
    amount: null,
    label: null,
    rhBuyingPower: null,
  };
  let lastPaper: { amount: number | null; label: string | null } = { amount: null, label: null };

  let paintedGeneration = -1;

  const load = (): void => {
    if (cancelled) return;
    if (inflight) {
      queued = true;
      return;
    }
    inflight = (async () => {
      const generation = accountScopeGeneration();
      const paper = paperAccountsEnabled();
      const cached = paper ? lastPaper : lastLive;
      paintHeaderBuyingPower(host, cached.amount, {
        paper,
        label: cached.label,
        rhBuyingPower: paper ? null : lastLive.rhBuyingPower,
      });
      await ensureAccountScopeCatalog();
      if (cancelled || accountScopeGeneration() !== generation) return;
      const accounts = scopedAccounts();
      if (paper) {
        const next = await paperBuyingPower(accounts);
        if (cancelled || accountScopeGeneration() !== generation) return;
        lastPaper = next;
        paintedGeneration = generation;
        paintHeaderBuyingPower(host, next.amount, { paper: true, label: next.label });
        return;
      }
      const enabled = await loadAgenticFeatureFlag();
      if (cancelled || accountScopeGeneration() !== generation) return;
      if (!enabled) {
        lastLive = { amount: null, label: null, rhBuyingPower: null };
        paintedGeneration = generation;
        paintHeaderBuyingPower(host, null, { paper: false, label: null });
        return;
      }
      const { snapshot } = await fetchAgenticSnapshot();
      if (cancelled || accountScopeGeneration() !== generation) return;
      lastLive = liveBuyingPower(snapshot, accounts);
      paintedGeneration = generation;
      paintHeaderBuyingPower(host, lastLive.amount, {
        paper: false,
        label: lastLive.label,
        rhBuyingPower: lastLive.rhBuyingPower,
      });
    })()
      .catch(() => {
        if (cancelled) return;
        const paperNow = paperAccountsEnabled();
        const last = paperNow ? lastPaper : lastLive;
        paintedGeneration = accountScopeGeneration();
        paintHeaderBuyingPower(host, last.amount, {
          paper: paperNow,
          label: last.label,
          rhBuyingPower: paperNow ? null : lastLive.rhBuyingPower,
        });
      })
      .finally(() => {
        inflight = null;
        if (cancelled) return;
        if (queued || accountScopeGeneration() !== paintedGeneration) {
          queued = false;
          load();
        }
      });
  };

  const loadLive = (): void => {
    if (cancelled) return;
    load();
    if (liveInflight) return;
    liveInflight = (async () => {
      try {
        const response = await refreshAgenticSnapshot();
        notifyAgenticSnapshotRefreshed(
          response.snapshot?.refreshedAt ?? response.snapshot?.capturedAt ?? null,
        );
      } catch {
        /* disconnected / rate-limited — still re-read the stored snapshot */
      }
      if (!cancelled) load();
    })().finally(() => {
      liveInflight = null;
    });
  };

  const onVisible = (): void => {
    if (document.visibilityState === "visible") load();
  };

  activeRefresh = load;
  activeLiveRefresh = loadLive;
  const unsubscribeScope = subscribeAccountScope(load);
  window.addEventListener(AGENTIC_SNAPSHOT_REFRESHED_EVENT, load);
  window.addEventListener(AGENTIC_ACCOUNT_CHANGED_EVENT, load);
  document.addEventListener("visibilitychange", onVisible);

  load();
  const timer = window.setInterval(load, HEADER_BP_POLL_MS);

  return () => {
    cancelled = true;
    if (activeRefresh === load) activeRefresh = null;
    if (activeLiveRefresh === loadLive) activeLiveRefresh = null;
    host.removeEventListener("click", onClick);
    unsubscribeScope();
    window.clearInterval(timer);
    window.removeEventListener(AGENTIC_SNAPSHOT_REFRESHED_EVENT, load);
    window.removeEventListener(AGENTIC_ACCOUNT_CHANGED_EVENT, load);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
